import { useState } from "preact/hooks";
import "./VerdictStrip.css";
import { useStore } from "../../state/useStore.ts";
import { K } from "../../state/keys.ts";
import type { PracticeMove } from "../../state/keys.ts";
import { verdictView, verdictDetails } from "../../domain/verdictView.ts";
import type { VerdictActionId } from "../../domain/verdictView.ts";
import { loadVerdictDetailsOpen, saveVerdictDetailsOpen } from "../../services/storage.ts";
import * as practice from "../../legacy/practice.js";
import { openLoginWall } from "../../legacy/practiceView.js";

const lastUserMove = (moves: (PracticeMove | undefined)[]): PracticeMove | null =>
    [...(moves || [])].reverse().find((m) => m && m.actor === "user") || null;

/**
 * The first row under the board (components/study/LivePanel.tsx): what the engine thought of the move just played,
 * how the practice ended, or whose turn it is — a row and not a card since 2026-11-01, because it is read with the
 * repertoire and the engine below it, every move.
 */
export function VerdictStrip() {
    const [detailsOpen, setDetailsOpen] = useState(loadVerdictDetailsOpen());

    const status = useStore(K.practice.status);
    const line = useStore(K.practice.line);
    const moves = useStore(K.practice.moves);
    const move = lastUserMove(moves);
    const isLoggedIn = !!useStore(K.user.currentUser);
    const turn = useStore(K.boards("study").turn);

    const view = verdictView({
        status, line, move,
        result: useStore(K.practice.result),
        offbook: useStore(K.practice.offbook),
        notice: useStore(K.practice.notice),
        engineLoading: useStore(K.engine.loadState) === "loading",
        detailsOpen,
        // The board knows whose turn it is; the line knows which side the user plays.
        userToMove: !!line && turn === line.color,
    });

    const run: Record<VerdictActionId, () => void> = {
        signUp: () => openLoginWall(),
        takeBack: () => practice.takeBack(),
        finishOffBook: () => practice.finishOffBook(),
        toggleDetails: () => {
            const open = !detailsOpen;
            setDetailsOpen(open);
            saveVerdictDetailsOpen(open);
        },
    };

    // The repertoire has a row of its own under this one: its line in the details would say it twice
    const rows = detailsOpen ? verdictDetails(move, isLoggedIn).filter((row) => row.label !== "Repertoire") : [];

    // Stopped off the book, the two ways on are the notice above (LivePanel's StopNotice), not two more buttons here
    const actions = status === "offbook"
        ? view.actions.filter((a) => a.id !== "takeBack" && a.id !== "finishOffBook") : view.actions;
    // Before the first move the board already says whose turn it is: the row says it in two words and no more
    const quiet = view.kind === "idle" && !view.move;

    return (
        <div class="live-row live-row--move move-verdict" data-verdict={view.kind} data-tone={view.tone} aria-live="polite">
            <dt class="live-row__label">Move</dt>
            {/* Changing the key recreates this node, which replays the stamp animation */}
            <dd key={view.stampKey} class={view.stamped ? "live-row__value move-verdict--stamp" : "live-row__value"}>
                <div class="live-move">
                    <span class="move-verdict__badge">{view.badge}</span>
                    {view.move && <span class="move-verdict__move">{view.move}</span>}
                    {actions.map((action) => (
                        <button
                            key={action.id}
                            type="button"
                            class={action.variant === "btn-link" ? "card__link live-move__action" : `btn ${action.variant} btn-sm live-move__action`}
                            onClick={run[action.id]}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
                {!quiet && view.insight && <p class="live-move__insight">{view.insight}</p>}
                {rows.length > 0 && (
                    <div class="live-move__details">
                        {rows.map((row) => (
                            <div key={row.label} class={row.muted ? "live-detail live-detail--muted" : "live-detail"}
                                 title={row.text}>
                                <span class="live-detail__label">{row.label}</span>
                                <span class="verdict-chips">
                                    <span class="visually-hidden">{row.text}</span>
                                    {row.mark && (
                                        <span class={`verdict-mark verdict-tone--${row.mark.tone}`} aria-hidden="true">
                                            {row.mark.glyph}
                                        </span>
                                    )}
                                    {row.chips.map((chip, index) => (
                                        <span key={index} aria-hidden="true"
                                              class={`verdict-chip verdict-tone--${chip.tone ?? "plain"}`
                                                  + (chip.strong ? " verdict-chip--strong" : "")}>
                                            {chip.lead && <span class="verdict-chip__lead">{chip.lead}</span>}
                                            {chip.text}
                                            {chip.tail && (
                                                <span class={`verdict-chip__tail verdict-tone--${chip.tail.tone}`}>
                                                    {chip.tail.text}
                                                </span>
                                            )}
                                        </span>
                                    ))}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </dd>
        </div>
    );
}
